import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, BookOpenCheck, Stethoscope } from 'lucide-react';
import { Link } from 'react-router-dom';
export function EditorialPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Editorial Policy & Medical Disclaimer | IntelliEats';
  }, []);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="pt-32 pb-24 bg-white min-h-screen" data-id="element-2508" data-mp="2vfqxewey5g2n">
      
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2509" data-mp="3o44u98uau6cm">
        <header className="mb-12" data-id="element-2510" data-mp="3o3dbwzppq54a">
          <Link to="/" className="inline-flex items-center text-sm font-medium text-brand-600 hover:text-brand-700 mb-8 transition-colors" data-id="element-2511" data-mp="3a4dmvqyafo7l">
            
            <ArrowLeft className="w-4 h-4 mr-2" data-id="element-2512" data-mp="3e4dzr75qnrcj" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-6" data-id="element-2513" data-mp="1yof9xbzzoi13">
            <div className="bg-brand-100 text-brand-600 p-3 rounded-2xl" data-id="element-2514" data-mp="22qk0u2z1z0k1">
              <BookOpenCheck className="w-8 h-8" data-id="element-2515" data-mp="22opfwons78fy" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-black text-slate-900 tracking-tight" data-id="element-2516" data-mp="3u7bc3p3oe332">
              Editorial Policy
            </h1>
          </div>
          <p className="text-lg text-slate-600 leading-relaxed" data-id="element-2517" data-mp="71kc37md0q35">
            How we research, write, and review the nutrition and ingredient
            content published across IntelliEats.
          </p>
          <p className="text-slate-500 text-sm mt-2" data-id="element-2518" data-mp="gzq8xatb7615">
            Last updated: July 9, 2026
          </p>
        </header>

        <div className="space-y-10 text-slate-700 leading-relaxed" data-id="element-2519" data-mp="bzvmbjgkh2lw">
          <section data-id="element-2520" data-mp="qpjlae19zp6g">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-2521" data-mp="qq2u958y738c">
              Our Editorial Mission
            </h2>
            <p data-id="element-2522" data-mp="ls1eat6tjk13">
              IntelliEats exists to help everyday shoppers understand what is in
              their packaged food. Every article, guide, and in-app explanation
              is written to be clear, accurate, and genuinely useful — never to
              create fear or promote a specific product. Our goal is awareness,
              not alarm.
            </p>
          </section>

          <section data-id="element-2523" data-mp="3tj9324cwh8mv">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-2524" data-mp="3tipmaqyzkqaf">
              How We Research Content
            </h2>
            <ul className="space-y-3 list-none p-0" data-id="element-2525" data-mp="65deb20leni8">
              {['We rely on reputable, authoritative sources such as the FDA, USDA, WHO, NIH, CDC, and peer-reviewed nutritional research.', 'Ingredient information reflects the current scientific consensus and clearly distinguishes established facts from areas where evidence is still emerging.', 'When a topic is debated (for example, certain additives or sweeteners), we present the balance of evidence rather than a one-sided claim.', 'We cite our primary references at the end of each article so you can read the source material yourself.'].map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-2526" data-mp="2qvme74m8tdt6" data-mpi={i}>
                  <span className="w-2 h-2 bg-brand-500 rounded-full mt-2 shrink-0" data-id="element-2527" data-mp="2qvw4yuf43ozx"></span>
                  <span data-id="element-2528" data-mp="2jdo3wxw8lyow">{item}</span>
                </li>)}
            </ul>
          </section>

          <section data-id="element-2529" data-mp="2eklcavb0szbc">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-2530" data-mp="2eja6kpy0lhhl">
              Accuracy & Updates
            </h2>
            <p data-id="element-2531" data-mp="25korj4rr4klk">
              Nutrition science evolves. We review our articles periodically and
              update them when guidelines, regulations, or the weight of
              evidence changes. If you spot something that appears inaccurate or
              out of date, please let us know and we will review it promptly.
            </p>
          </section>

          <section data-id="element-2532" data-mp="2t8pve0dzjcun">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-2533" data-mp="2tcdmksvyjn67">
              Independence & Transparency
            </h2>
            <p data-id="element-2534" data-mp="1huui6x6n5e62">
              Our ingredient assessments are made independently and are not
              influenced by food manufacturers or advertisers. Where a page
              contains advertising, it is clearly part of the page layout and
              does not shape our editorial judgment about any ingredient.
            </p>
          </section>

          <section className="bg-amber-50 rounded-2xl p-8 border border-amber-100" data-id="element-2535" data-mp="35kql6hqviffn">
            <div className="flex items-center gap-3 mb-4" data-id="element-2536" data-mp="2rtc4wiocosca">
              <div className="bg-amber-100 text-amber-700 p-2 rounded-xl" data-id="element-2537" data-mp="2vt4e8xlkj2ip">
                <Stethoscope className="w-6 h-6" data-id="element-2538" data-mp="2vr9pt9b38s9v" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900" data-id="element-2539" data-mp="3jjqdbzdlhcgk">
                Medical Disclaimer
              </h2>
            </div>
            <p className="mb-4" data-id="element-2540" data-mp="3aiinm5p59jy3">
              All content produced by IntelliEats — including articles, guides,
              and the in-app ingredient analysis — is provided for{' '}
              <span className="font-semibold text-slate-900" data-id="element-2541" data-mp="3fjg4meyf47y0">
                educational and informational purposes only
              </span>
              . It is not a substitute for professional medical, nutritional, or
              dietary advice, diagnosis, or treatment.
            </p>
            <p className="mb-4" data-id="element-2542" data-mp="1vniqdvsplx1a">
              Ingredients approved by regulatory authorities are generally
              considered safe when consumed within established limits.
              IntelliEats helps you make informed decisions, but it does not
              diagnose conditions or provide personalized medical guidance.
            </p>
            <p className="m-0" data-id="element-2543" data-mp="1cu6jghfgicuy">
              Always seek the advice of a qualified healthcare professional or
              registered dietitian with any questions you may have regarding a
              medical condition, allergy, or your individual dietary needs.
              Never disregard professional advice because of something you read
              here.
            </p>
          </section>

          <section className="bg-brand-50 rounded-2xl p-8 border border-brand-100" data-id="element-2544" data-mp="u3oumnxqw1ev">
            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-2545" data-mp="p5vofuwfz7el">
              Questions or Corrections
            </h2>
            <p className="mb-4" data-id="element-2546" data-mp="2p6ttvgi3xy5d">
              If you have questions about our editorial process or would like to
              suggest a correction, reach out anytime:
            </p>
            <p className="font-medium text-slate-900" data-id="element-2547" data-mp="2yx6ch36d3h8v">
              Email:{' '}
              <a href="mailto:intellieats@gmail.com" className="text-brand-600 hover:text-brand-700 transition-colors" data-id="element-2548" data-mp="32yrtn0pxihov">
                
                intellieats@gmail.com
              </a>
            </p>
            <p className="mt-2" data-id="element-2549" data-mp="2b7c34vl94atd">
              Or use our{' '}
              <Link to="/contact" className="text-brand-600 hover:text-brand-700 font-medium transition-colors" data-id="element-2550" data-mp="2b9orbqhlrhev">
                
                Contact Support
              </Link>{' '}
              page.
            </p>
          </section>
        </div>
      </div>
    </motion.div>;
}